import React, { useCallback, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import { CircularProgress, Dialog } from "@material-ui/core";
import {
  CallSplit,
  CheckCircle,
  Close,
  CloudUploadOutlined,
  ErrorOutline,
  GetApp,
  InfoOutlined,
  LibraryBooksOutlined,
  OpenInNew,
  Refresh,
  Search,
  Visibility,
} from "@material-ui/icons";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import CustomSelect from "../../NoticeGeneration/component/CustomSelect";
import useLoader from "../../../../hooks/useLoader";
import AWSService from "../../../../services/AWSService";
import EstampService from "../../../../services/EstampService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import { PageWrapper, Label } from "./SplitAndUpload";

const PDF_TYPES = ["application/pdf"];
const EXCEL_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];
const POLL_MS = 5000;
const MAX_POLL_FAILURES = 5;
const DONE_STATUSES = ["completed", "failed"];
const EMPTY_BATCH = {
  recordId: "",
  count: "",
  documentFile: "",
  excelFile: "",
};

const EMPTY_VIEWER = {
  open: false,
  loading: false,
  src: "",
  url: "",
  name: "",
};
const LOAN_NUMBER_HEADERS = ["Contract Number", "Loan No", "Loan Number"];
const PAIR_SEARCH_MIN = 5;

const checkLoanNumberHeader = async (file) => {
  const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const [headers = []] = ws ? XLSX.utils.sheet_to_json(ws, { header: 1 }) : [];
  if (headers.some((h) => LOAN_NUMBER_HEADERS.includes(h))) return "";
  const found = headers
    .filter((h) => h != null && h !== "")
    .map((h) => `"${h}"`);
  return `Invalid Excel header. Expected exactly one of: ${LOAN_NUMBER_HEADERS.join(
    ", ",
  )}${found.length ? `. Found: ${found.join(", ")}` : ""}`;
};

const isJobRunning = (status) => status && !DONE_STATUSES.includes(status);

const hasRunningJob = (record) =>
  record.merge_job_status === "submitting" ||
  (record.merge_job_id && isJobRunning(record.merge_job_status));

const toTrackedJob = (record) => ({
  id: record.id,
  status: record.merge_job_status,
  data: record,
  failures: 0,
  progress: null,
});

const matchesPair = (record, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [record.first_party, record.second_party].some((v) =>
    String(v || "")
      .toLowerCase()
      .includes(q),
  );
};

const getJobProgress = (job) => {
  if (!job?.status || job.status === "failed") return null;
  const hasCounts = typeof job.total === "number" && job.total > 0;
  const done = Math.min(job.number ?? 0, job.total);
  if (job.status === "completed")
    return hasCounts
      ? { percent: 100, number: job.total, total: job.total }
      : { percent: 100 };
  if (hasCounts)
    return {
      percent: Math.round((done / job.total) * 100),
      number: done,
      total: job.total,
    };
  if (typeof job.progress === "number") return { percent: job.progress };
  return { percent: null };
};

const StatusPill = ({ status }) => {
  if (!status) return <Pill $tone="muted">No job yet</Pill>;
  if (status === "completed")
    return (
      <Pill $tone="success">
        <CheckCircle style={{ fontSize: 14 }} /> Completed
      </Pill>
    );
  if (status === "failed")
    return (
      <Pill $tone="danger">
        <ErrorOutline style={{ fontSize: 14 }} /> Failed
      </Pill>
    );
  return (
    <Pill $tone="warning">
      <CircularProgress size={11} style={{ color: "inherit" }} />{" "}
      {status === "pending" ? "Pending" : "Processing"}
    </Pill>
  );
};

const EstampStock = () => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [party, setParty] = useState({ id: "", name: "" });
  const [stock, setStock] = useState({ data: [], summary: {} });
  const [stockLoading, setStockLoading] = useState(false);
  const [estampFile, setEstampFile] = useState("");
  const [preview, setPreview] = useState(null);
  const [batch, setBatch] = useState(EMPTY_BATCH);
  const [batchError, setBatchError] = useState(null);
  const [job, setJob] = useState(null);
  const [viewer, setViewer] = useState(EMPTY_VIEWER);
  const [stockSearch, setStockSearch] = useState("");
  const [pairSearch, setPairSearch] = useState("");

  const notifyError = (error) =>
    enqueueSnackbar(getErrorMessage(error), { variant: "error" });

  const loadStock = useCallback(
    async (partyId) => {
      if (!partyId) return;
      try {
        setStockLoading(true);
        const res = await EstampService.partyStock(partyId);
        setStock({ data: res?.data || [], summary: res?.summary || {} });
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      } finally {
        setStockLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const selectParty = (name, id) => {
    setParty({ id, name });
    setStock({ data: [], summary: {} });
    setEstampFile("");
    setPreview(null);
    setBatch(EMPTY_BATCH);
    setBatchError(null);
    setJob(null);
    setStockSearch("");
    setPairSearch("");
    loadStock(id);
  };

  useEffect(() => {
    if (!viewer.src) return;
    return () => URL.revokeObjectURL(viewer.src);
  }, [viewer.src]);

  async function openExtractedPages() {
    const url = preview?.confirm_data?.output_pdf_url;
    if (!url) return;
    setViewer({
      open: true,
      loading: true,
      src: "",
      url,
      name: getFileName(url) || "extracted-pages.pdf",
    });
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Could not load the PDF (${res.status})`);
      const blob = new Blob([await res.blob()], { type: "application/pdf" });
      const src = URL.createObjectURL(blob);
      setViewer((v) => {
        if (!v.open || v.url !== url) {
          URL.revokeObjectURL(src);
          return v;
        }
        return { ...v, loading: false, src };
      });
    } catch (error) {
      setViewer((v) => (v.url === url ? { ...v, loading: false } : v));
      notifyError(error);
    }
  }

  const closeViewer = () => setViewer(EMPTY_VIEWER);

  useEffect(() => {
    if (!job?.id || !isJobRunning(job.status)) return;
    if (job.failures >= MAX_POLL_FAILURES) return;
    let cancelled = false;
    const timer = setTimeout(
      async () => {
        try {
          const res = await EstampService.jobStatus(job.id);
          if (cancelled) return;
          setJob((current) => ({
            ...current,
            status: res?.status,
            data: res?.data,
            progress:
              typeof res?.progress === "number"
                ? res.progress
                : current.progress,
            number:
              typeof res?.number === "number" ? res.number : current.number,
            total: typeof res?.total === "number" ? res.total : current.total,
            failures: 0,
            polled: true,
          }));
          if (res?.status === "completed") {
            enqueueSnackbar("E-stamp merge job completed", {
              variant: "success",
            });
            loadStock(party.id);
          } else if (res?.status === "failed") {
            enqueueSnackbar(res?.error || "E-stamp merge job failed", {
              variant: "error",
            });
            loadStock(party.id);
          }
        } catch (error) {
          if (!cancelled)
            setJob((current) => ({
              ...current,
              failures: (current?.failures || 0) + 1,
              polled: true,
            }));
        }
      },
      job.polled ? POLL_MS : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [job, party.id, loadStock, enqueueSnackbar]);

  async function s3Upload(file, types, onUploaded, validate) {
    if (!file) return;
    if (!types.includes(file.type)) {
      enqueueSnackbar("Invalid file type", { variant: "error" });
      return;
    }
    if (validate) {
      try {
        const message = await validate(file);
        if (message) {
          enqueueSnackbar(message, { variant: "error" });
          return;
        }
      } catch (error) {
        enqueueSnackbar("Could not read the Excel file", { variant: "error" });
        return;
      }
    }
    try {
      setLoader({ state: true, message: `Uploading file ${file.name}...` });
      const s3Response = await AWSService.getS3URL({ key: file.name });
      if (s3Response?.url) {
        onUploaded(await AWSService.uploadToS3(s3Response.url, file));
      }
    } catch (error) {
      notifyError(error);
    } finally {
      setLoader({ state: false });
    }
  }

  async function processEstamp() {
    try {
      setLoader({ state: true, message: "Reading the e-stamp..." });
      const res = await EstampService.preview({
        partyId: party.id,
        estampFile,
      });
      setPreview(res);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoader({ state: false });
    }
  }

  async function confirmEstamp() {
    try {
      setLoader({ state: true, message: "Saving the e-stamp..." });
      const res = await EstampService.confirm(preview.confirm_data);
      enqueueSnackbar(res?.message || "E-stamp saved", { variant: "success" });
      setPreview(null);
      setEstampFile("");
      loadStock(party.id);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoader({ state: false });
    }
  }

  const availableStock = stock.data.filter((r) => r.total_pages > 0);
  const visiblePairs = availableStock.filter((r) => matchesPair(r, pairSearch));
  const visibleStock = stock.data.filter((r) => matchesPair(r, stockSearch));
  const progress = getJobProgress(job);
  const runningRecord = stock.data.find(hasRunningJob);
  const selectedRecord = stock.data.find((r) => r.id === batch.recordId);
  const count = Number(batch.count);
  const countError =
    batch.count !== "" &&
    selectedRecord &&
    (count < 1 || count > selectedRecord.total_pages)
      ? `Enter a number from 1 to ${selectedRecord.total_pages}`
      : "";
  const canSplit =
    selectedRecord &&
    batch.count !== "" &&
    !countError &&
    batch.documentFile &&
    batch.excelFile &&
    !runningRecord &&
    !isJobRunning(job?.status);

  async function splitBatch() {
    try {
      setBatchError(null);
      setLoader({
        state: true,
        message: "Checking files and splitting e-stamp pages...",
      });
      const res = await EstampService.splitBatch({
        partyId: party.id,
        count,
        documentFile: batch.documentFile,
        excelFile: batch.excelFile,
        first_party: selectedRecord.first_party,
        second_party: selectedRecord.second_party,
      });
      enqueueSnackbar(res?.message || "Batch submitted", {
        variant: "success",
      });
      setJob({
        id: res?.data?.id,
        status: res?.data?.merge_job_status || "processing",
        data: res?.data,
        failures: 0,
        progress: null,
      });
      setBatch(EMPTY_BATCH);
      loadStock(party.id);
    } catch (error) {
      setBatchError({
        message: getErrorMessage(error),
        errors: error?.errors || [],
        summary: error?.summary,
      });
      if (error?.code === "JOB_RUNNING") {
        if (error.data) trackRecord(error.data);
        loadStock(party.id);
      }
    } finally {
      setLoader({ state: false });
    }
  }

  const trackRecord = (record) => setJob(toTrackedJob(record));

  const runningRecordId = runningRecord?.id;
  useEffect(() => {
    if (!runningRecordId) return;
    setJob((current) =>
      isJobRunning(current?.status) ? current : toTrackedJob(runningRecord),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningRecordId]);

  return (
    <PageWrapper>
      <Hero>
        <HeroText>
          <HeroTitle>E-Stamp Stock</HeroTitle>
          <HeroSub>
            Add e-stamp papers to a party's stock, then use them for an award
            batch.
          </HeroSub>
        </HeroText>
        <PartyPicker>
          <CustomSelect
            name="partyId"
            value={party.id}
            trigger
            onPartySelect={selectParty}
            setFieldValue={(_, value) => !value && selectParty("", "")}
            errors={{}}
            touched={{}}
            menuItems={[]}
          />
        </PartyPicker>
      </Hero>

      {!party.id ? (
        <EmptyState>
          <LibraryBooksOutlined
            style={{ fontSize: 40, color: COLORS.BTN_GREEN }}
          />
          <EmptyTitle>Select a party to get started</EmptyTitle>
          <EmptySub>
            You will see the party's e-stamp stock and can add or use e-stamps.
          </EmptySub>
        </EmptyState>
      ) : (
        <>
          <Stats>
            <StatTile>
              <StatValue>{stock.summary.available_pages ?? 0}</StatValue>
              <StatLabel>Pages available</StatLabel>
            </StatTile>
            <StatTile>
              <StatValue>{stock.summary.used_pages ?? 0}</StatValue>
              <StatLabel>Pages used</StatLabel>
            </StatTile>
            <StatTile>
              <StatValue>{stock.data.length}</StatValue>
              <StatLabel>First / second party pairs</StatLabel>
            </StatTile>
          </Stats>

          <Columns>
            {/* ── Step 1: add e-stamp ── */}
            <Panel>
              <PanelHead>
                <StepBadge>1</StepBadge>
                <div>
                  <PanelTitle>Add e-stamp</PanelTitle>
                  <PanelSub>
                    Upload the e-stamp PDF, check the details, then save
                  </PanelSub>
                </div>
              </PanelHead>

              {!preview ? (
                <>
                  <Label>
                    E-stamp PDF <Required>*</Required>
                  </Label>
                  <CustomFileUpload
                    style={{ width: "100%", boxSizing: "border-box" }}
                    accept=".pdf"
                    file={{ name: getFileName(estampFile) }}
                    fileUrl={estampFile}
                    setFile={(file) => s3Upload(file, PDF_TYPES, setEstampFile)}
                  />
                  <Actions>
                    <PrimaryBtn
                      type="button"
                      disabled={!estampFile}
                      onClick={processEstamp}
                    >
                      <CloudUploadOutlined style={{ fontSize: 18 }} />
                      Process e-stamp
                    </PrimaryBtn>
                  </Actions>
                </>
              ) : (
                <>
                  <ReportGrid>
                    <ReportRow $wide>
                      <span>Party</span>
                      <strong>{preview.report?.party_name}</strong>
                    </ReportRow>
                    <ReportRow $wide>
                      <span>First party</span>
                      <strong>{preview.report?.first_party}</strong>
                    </ReportRow>
                    <ReportRow $wide>
                      <span>Second party</span>
                      <strong>{preview.report?.second_party}</strong>
                    </ReportRow>
                    <ReportRow>
                      <span>Pages in stock</span>
                      <strong>{preview.report?.pages_before}</strong>
                    </ReportRow>
                    <ReportRow>
                      <span>Pages to add</span>
                      <strong className="plus">
                        +{preview.report?.pages_added}
                      </strong>
                    </ReportRow>
                    <ReportRow>
                      <span>Stock after save</span>
                      <strong>{preview.report?.total_pages_now}</strong>
                    </ReportRow>
                    <ReportRow>
                      <span>Pages used so far</span>
                      <strong>{preview.report?.used_pages}</strong>
                    </ReportRow>
                  </ReportGrid>
                  <LinkBtn
                    type="button"
                    disabled={!preview.confirm_data?.output_pdf_url}
                    onClick={openExtractedPages}
                  >
                    <Visibility style={{ fontSize: 16 }} /> View extracted pages
                  </LinkBtn>
                  <Actions>
                    <GhostBtn type="button" onClick={() => setPreview(null)}>
                      Cancel
                    </GhostBtn>
                    <PrimaryBtn type="button" onClick={confirmEstamp}>
                      <CheckCircle style={{ fontSize: 18 }} />
                      Confirm and save
                    </PrimaryBtn>
                  </Actions>
                </>
              )}
            </Panel>

            {/* ── Step 2: use stock for a batch ── */}
            <Panel>
              <PanelHead>
                <StepBadge>2</StepBadge>
                <div>
                  <PanelTitle>Use e-stamps for a batch</PanelTitle>
                  <PanelSub>
                    Pick the stock, enter the award count and upload the files
                  </PanelSub>
                </div>
              </PanelHead>

              {availableStock.length === 0 ? (
                <InlineNote>
                  No pages available for this party. Add an e-stamp first.
                </InlineNote>
              ) : (
                <>
                  <LabelRow>
                    <Label>
                      E-stamp stock <Required>*</Required>
                    </Label>
                    <CountText>
                      {pairSearch
                        ? `${visiblePairs.length} of ${availableStock.length}`
                        : availableStock.length}{" "}
                      {availableStock.length === 1 ? "pair" : "pairs"}
                    </CountText>
                  </LabelRow>
                  {availableStock.length > PAIR_SEARCH_MIN && (
                    <SearchBox $block>
                      <Search style={{ fontSize: 18 }} />
                      <input
                        type="search"
                        placeholder="Search first or second party"
                        value={pairSearch}
                        onChange={(e) => setPairSearch(e.target.value)}
                      />
                    </SearchBox>
                  )}
                  <PairList>
                    {visiblePairs.length === 0 && (
                      <InlineNote>No stock matches "{pairSearch}".</InlineNote>
                    )}
                    {visiblePairs.map((record) => (
                      <PairOption
                        key={record.id}
                        type="button"
                        $selected={record.id === batch.recordId}
                        onClick={() => {
                          setBatch((b) => ({ ...b, recordId: record.id }));
                          setBatchError(null);
                        }}
                      >
                        <PairNames>
                          <strong>{record.first_party}</strong>
                          <span>{record.second_party}</span>
                        </PairNames>
                        <PagesChip>{record.total_pages} pages</PagesChip>
                      </PairOption>
                    ))}
                  </PairList>
                  {selectedRecord && (
                    <SelectedLine>
                      Selected: <strong>{selectedRecord.first_party}</strong> →{" "}
                      {selectedRecord.second_party} ·{" "}
                      {selectedRecord.total_pages} pages
                    </SelectedLine>
                  )}

                  <FieldGrid>
                    <div>
                      <Label>
                        No. of awards <Required>*</Required>
                      </Label>
                      <CustomInputField
                        variant="outlined"
                        type="text"
                        placeholder={
                          selectedRecord
                            ? "Enter a number, e.g. 2"
                            : "Select stock first"
                        }
                        disabled={!selectedRecord}
                        value={batch.count}
                        error={!!countError}
                        helperText={countError || " "}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => {
                          // digits only — blocks "-", "e", "." and ranges like "1-2"
                          const value = e.target.value.replace(/\D/g, "");
                          setBatch((b) => ({ ...b, count: value }));
                        }}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <Label>
                        Awards PDF <Required>*</Required>
                      </Label>
                      <CustomFileUpload
                        style={{ width: "100%", boxSizing: "border-box" }}
                        accept=".pdf"
                        file={{ name: getFileName(batch.documentFile) }}
                        fileUrl={batch.documentFile}
                        setFile={(file) =>
                          s3Upload(file, PDF_TYPES, (url) =>
                            setBatch((b) => ({ ...b, documentFile: url })),
                          )
                        }
                      />
                    </div>
                    <div>
                      <Label>
                        Stamp paper list (Excel) <Required>*</Required>
                      </Label>
                      <CustomFileUpload
                        style={{ width: "100%", boxSizing: "border-box" }}
                        accept=".xlsx, .xls"
                        file={{ name: getFileName(batch.excelFile) }}
                        fileUrl={batch.excelFile}
                        setFile={(file) =>
                          s3Upload(
                            file,
                            EXCEL_TYPES,
                            (url) =>
                              setBatch((b) => ({ ...b, excelFile: url })),
                            checkLoanNumberHeader,
                          )
                        }
                      />
                      <FieldHint>
                        <InfoOutlined style={{ fontSize: 14 }} />
                        <span>
                          Excel must have one of these columns:{" "}
                          {LOAN_NUMBER_HEADERS.map((h, i) => (
                            <React.Fragment key={h}>
                              {i > 0 && ", "}
                              <strong>{h}</strong>
                            </React.Fragment>
                          ))}
                        </span>
                      </FieldHint>
                    </div>
                  </FieldGrid>

                  {batchError && (
                    <ErrorBox>
                      <strong>{batchError.message}</strong>
                      {batchError.errors?.length > 0 && (
                        <ul>
                          {batchError.errors.map((e) => (
                            <li key={e}>{e}</li>
                          ))}
                        </ul>
                      )}
                      {batchError.summary && (
                        <SummaryLine>
                          PDF documents: {batchError.summary.pdf_documents} ·
                          Excel rows: {batchError.summary.excel_rows} · Matched:{" "}
                          {batchError.summary.matched}
                        </SummaryLine>
                      )}
                    </ErrorBox>
                  )}

                  {runningRecord && (
                    <BusyNote role="status">
                      <InfoOutlined style={{ fontSize: 16 }} />
                      <span>
                        A merge job is running for this party (
                        <strong>{runningRecord.first_party}</strong> →{" "}
                        {runningRecord.second_party}). You can submit a new
                        batch when it finishes.
                      </span>
                    </BusyNote>
                  )}

                  <Actions>
                    <PrimaryBtn
                      type="button"
                      disabled={!canSplit}
                      onClick={splitBatch}
                    >
                      <CallSplit style={{ fontSize: 18 }} />
                      Submit
                    </PrimaryBtn>
                  </Actions>
                </>
              )}

              {job?.id && (
                <JobCard $status={job.status}>
                  <JobHead>
                    <div>
                      <JobTitle>Merge job</JobTitle>
                      <JobMeta>
                        {job.data?.first_party} → {job.data?.second_party}
                      </JobMeta>
                    </div>
                    <StatusPill status={job.status} />
                  </JobHead>
                  {progress && (
                    <ProgressWrap>
                      <ProgressText>
                        <span>
                          {progress.percent === null
                            ? "Waiting for progress from the server..."
                            : `${progress.percent}% complete`}
                        </span>
                        {progress.total ? (
                          <em>
                            {progress.number} of {progress.total} completed
                          </em>
                        ) : null}
                      </ProgressText>
                      <ProgressTrack
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={progress.percent ?? undefined}
                        aria-valuetext={
                          progress.total
                            ? `${progress.number} of ${progress.total} completed`
                            : undefined
                        }
                      >
                        <ProgressFill
                          $percent={progress.percent}
                          $done={job.status === "completed"}
                        />
                      </ProgressTrack>
                    </ProgressWrap>
                  )}
                  {isJobRunning(job.status) && (
                    <JobMeta>
                      {job.failures >= MAX_POLL_FAILURES
                        ? "Could not reach the server. Check the status again."
                        : "Checking every few seconds. You can stay on this page or come back later."}
                    </JobMeta>
                  )}
                  {job.status === "failed" && (
                    <JobMeta>
                      {job.data?.pages_held_by_job
                        ? `${job.data.pages_held_by_job} e-stamp page(s) could not be put back into the stock yet. Click Refresh to try again.`
                        : "The e-stamp pages are back in the stock. You can submit this batch again."}
                    </JobMeta>
                  )}
                  <Actions>
                    {isJobRunning(job.status) &&
                      job.failures >= MAX_POLL_FAILURES && (
                        <GhostBtn
                          type="button"
                          onClick={() =>
                            setJob((j) => ({
                              ...j,
                              failures: 0,
                              polled: false,
                            }))
                          }
                        >
                          <Refresh style={{ fontSize: 16 }} /> Check again
                        </GhostBtn>
                      )}
                    {job.status === "completed" &&
                      job.data?.final_output_zip_url && (
                        <PrimaryBtn
                          as="a"
                          href={job.data.final_output_zip_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <GetApp style={{ fontSize: 18 }} /> Download output
                        </PrimaryBtn>
                      )}
                  </Actions>
                </JobCard>
              )}
            </Panel>
          </Columns>

          {/* ── Stock list ── */}
          <Panel>
            <PanelHead $spread>
              <div>
                <PanelTitle>Stock for {party.name}</PanelTitle>
                <PanelSub>One row per first party / second party pair</PanelSub>
              </div>
              <HeadTools>
                <SearchBox>
                  <Search style={{ fontSize: 18 }} />
                  <input
                    type="search"
                    placeholder="Search first or second party"
                    value={stockSearch}
                    onChange={(e) => setStockSearch(e.target.value)}
                  />
                </SearchBox>
                <GhostBtn
                  type="button"
                  onClick={() => loadStock(party.id)}
                  disabled={stockLoading}
                >
                  <Refresh style={{ fontSize: 16 }} /> Refresh
                </GhostBtn>
              </HeadTools>
            </PanelHead>

            {stockLoading ? (
              <InlineNote>
                <CircularProgress size={16} /> Loading stock...
              </InlineNote>
            ) : stock.data.length === 0 ? (
              <InlineNote>No e-stamps saved for this party yet.</InlineNote>
            ) : visibleStock.length === 0 ? (
              <InlineNote>No stock matches "{stockSearch}".</InlineNote>
            ) : (
              <TableScroll>
                <StockTable>
                  <thead>
                    <tr>
                      <th>First party</th>
                      <th>Second party</th>
                      <th className="num">Available</th>
                      <th className="num">Used</th>
                      <th>Last job</th>
                      <th>Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStock.map((record) => (
                      <tr key={record.id}>
                        <td data-label="First party">{record.first_party}</td>
                        <td data-label="Second party">{record.second_party}</td>
                        <td data-label="Available" className="num">
                          <PagesChip $muted={!record.total_pages}>
                            {record.total_pages}
                          </PagesChip>
                        </td>
                        <td data-label="Used" className="num">
                          {record.used_pages || 0}
                        </td>
                        <td data-label="Last job">
                          <StatusPill
                            status={
                              record.merge_job_id
                                ? record.merge_job_status
                                : null
                            }
                          />
                        </td>
                        <td data-label="Files">
                          <FileLinks>
                            {record.merged_pdf_url && (
                              <a
                                href={record.merged_pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Stock PDF
                              </a>
                            )}
                            {record.used_pdf_url && (
                              <a
                                href={record.used_pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Used PDF
                              </a>
                            )}
                            {record.merge_job_status === "completed" &&
                              record.final_output_zip_url && (
                                <a
                                  href={record.final_output_zip_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Output
                                </a>
                              )}
                            {record.merge_job_id &&
                              isJobRunning(record.merge_job_status) && (
                                <button
                                  type="button"
                                  onClick={() => trackRecord(record)}
                                >
                                  Track job
                                </button>
                              )}
                          </FileLinks>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StockTable>
              </TableScroll>
            )}
            {stockSearch && visibleStock.length > 0 && (
              <CountText style={{ marginTop: 10 }}>
                Showing {visibleStock.length} of {stock.data.length}
              </CountText>
            )}
          </Panel>
        </>
      )}

      <Dialog open={viewer.open} onClose={closeViewer} maxWidth="lg" fullWidth>
        <ViewerHead>
          <ViewerTitle title={viewer.name}>Extracted pages</ViewerTitle>
          <ViewerActions>
            {viewer.src ? (
              <PrimaryBtn as="a" href={viewer.src} download={viewer.name}>
                <GetApp style={{ fontSize: 18 }} /> Download
              </PrimaryBtn>
            ) : (
              !viewer.loading &&
              viewer.url && (
                <GhostBtn
                  as="a"
                  href={viewer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNew style={{ fontSize: 16 }} /> Open in new tab
                </GhostBtn>
              )
            )}
            <IconBtn type="button" aria-label="Close" onClick={closeViewer}>
              <Close />
            </IconBtn>
          </ViewerActions>
        </ViewerHead>
        <ViewerBody>
          {viewer.loading ? (
            <InlineNote>
              <CircularProgress size={16} /> Loading preview...
            </InlineNote>
          ) : viewer.src ? (
            <iframe src={viewer.src} title="Extracted pages preview" />
          ) : (
            <InlineNote>Could not load the preview.</InlineNote>
          )}
        </ViewerBody>
      </Dialog>
    </PageWrapper>
  );
};

export default EstampStock;

const GREEN = COLORS.BTN_GREEN;

const Hero = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 24px 28px;
  margin-bottom: 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdfa 100%);
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(41, 52, 97, 0.06);
`;

const HeroText = styled.div`
  min-width: 220px;
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const HeroSub = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
`;

const PartyPicker = styled.div`
  width: 360px;
  max-width: 100%;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 56px 16px;
  border-radius: 16px;
  border: 1px dashed #cbd5e1;
  background: #fafbfc;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontBold};
`;

const EmptySub = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
`;

const StatTile = styled.div`
  padding: 16px 20px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
`;

const StatValue = styled.div`
  font-size: 26px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const StatLabel = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
  gap: 20px;
  align-items: start;
  margin-bottom: 20px;
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(41, 52, 97, 0.05);
  min-width: 0;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  ${({ $spread }) =>
    $spread &&
    css`
      justify-content: space-between;
      flex-wrap: wrap;
    `}
`;

const StepBadge = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${GREEN};
  color: #ffffff;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontBold};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontBold};
`;

const PanelSub = styled.div`
  margin-top: 2px;
  font-size: 12.5px;
  color: #64748b;
`;

const Required = styled.span`
  color: #ef4444;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontBold};
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled.button`
  ${buttonBase}
  border: 1px solid ${GREEN};
  background: ${GREEN};
  color: #ffffff;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }
`;

const GhostBtn = styled.button`
  ${buttonBase}
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: ${COLORS.COLOR_DARK};

  &:hover:not(:disabled) {
    border-color: ${GREEN};
    color: ${GREEN};
  }
`;

const LinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: ${GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-decoration: none;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: none;
  color: #64748b;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
    color: ${COLORS.COLOR_DARK};
  }
`;

const ViewerHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 14px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ViewerTitle = styled.div`
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontBold};
`;

const ViewerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewerBody = styled.div`
  padding: 16px;
  background: #f8fafc;

  iframe {
    display: block;
    width: 100%;
    height: 75vh;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
  }
`;

const HeadTools = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  background: #ffffff;
  color: #94a3b8;
  box-sizing: border-box;
  width: ${({ $block }) => ($block ? "100%" : "260px")};
  max-width: 100%;
  margin-bottom: ${({ $block }) => ($block ? "10px" : "0")};

  &:focus-within {
    border-color: ${GREEN};
  }
  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: none;
    font-size: 13px;
    color: ${COLORS.COLOR_DARK};
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const CountText = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const SelectedLine = styled.div`
  margin: -8px 0 18px;
  font-size: 12.5px;
  color: #64748b;
  word-break: break-word;

  strong {
    color: ${COLORS.COLOR_DARK};
  }
`;

const FieldHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 6px;
  font-size: 11.5px;
  line-height: 1.4;
  color: #64748b;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
  strong {
    color: ${COLORS.COLOR_DARK};
  }
`;

const ProgressWrap = styled.div`
  margin-top: 12px;
`;

const ProgressText = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 12.5px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};

  em {
    font-style: normal;
    font-size: 11.5px;
    color: #64748b;
  }
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 99px;
  background: rgba(100, 116, 139, 0.15);
  overflow: hidden;
`;

const indeterminate = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $percent }) => ($percent === null ? 40 : $percent)}%;
  border-radius: 99px;
  background: ${({ $done }) => ($done ? "#10b981" : "#f59e0b")};
  transition: width 0.6s ease;
  ${({ $percent }) =>
    $percent === null &&
    css`
      animation: ${indeterminate} 1.4s ease-in-out infinite;
    `}
`;

const TableScroll = styled.div`
  max-height: 520px;
  overflow-y: auto;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  @media (max-width: 760px) {
    max-height: none;
  }
`;

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const ReportRow = styled.div`
  grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef2f6;

  span {
    font-size: 11.5px;
    color: #64748b;
  }
  strong {
    font-size: 14px;
    color: ${COLORS.COLOR_DARK};
    word-break: break-word;
  }
  strong.plus {
    color: ${GREEN};
  }
`;

const InlineNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
`;

const PairList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 264px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin-bottom: 18px;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 99px;
  }
`;

const PairOption = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  text-align: left;
  cursor: pointer;
  border: 1.5px solid ${({ $selected }) => ($selected ? GREEN : "#e5e7eb")};
  background: ${({ $selected }) => ($selected ? `${GREEN}0f` : "#ffffff")};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${GREEN};
  }
`;

const PairNames = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  strong {
    font-size: 13.5px;
    color: ${COLORS.COLOR_DARK};
  }
  span {
    font-size: 12px;
    color: #64748b;
  }
`;

const PagesChip = styled.span`
  flex-shrink: 0;
  display: inline-block;
  padding: 3px 10px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $muted }) => ($muted ? "#f1f5f9" : `${GREEN}14`)};
  color: ${({ $muted }) => ($muted ? "#94a3b8" : GREEN)};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const ErrorBox = styled.div`
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 13px;

  ul {
    margin: 8px 0 0;
    padding-left: 18px;
  }
  li {
    margin-top: 4px;
    word-break: break-word;
  }
`;

const BusyNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 13px;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
  strong {
    word-break: break-word;
  }
`;

const SummaryLine = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #7f1d1d;
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.25); }
  70% { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
`;

const JobCard = styled.div`
  margin-top: 20px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid
    ${({ $status }) =>
      $status === "completed"
        ? "#a7f3d0"
        : $status === "failed"
          ? "#fecaca"
          : "#fde68a"};
  background: ${({ $status }) =>
    $status === "completed"
      ? "#ecfdf5"
      : $status === "failed"
        ? "#fef2f2"
        : "#fffbeb"};
  ${({ $status }) =>
    isJobRunning($status) &&
    css`
      animation: ${pulse} 2s infinite;
    `}

  ${Actions} {
    margin-top: 12px;
  }
`;

const JobHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const JobTitle = styled.div`
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontBold};
`;

const JobMeta = styled.div`
  margin-top: 4px;
  font-size: 12.5px;
  color: #64748b;
  word-break: break-word;
`;

const PILL_TONES = {
  success: { bg: "#ecfdf5", fg: "#047857" },
  danger: { bg: "#fef2f2", fg: "#b91c1c" },
  warning: { bg: "#fffbeb", fg: "#b45309" },
  muted: { bg: "#f1f5f9", fg: "#64748b" },
};

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $tone }) => PILL_TONES[$tone].bg};
  color: ${({ $tone }) => PILL_TONES[$tone].fg};
`;

const StockTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 12px;
    background: #f8fafc;
    color: #64748b;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    color: ${COLORS.COLOR_DARK};
    vertical-align: middle;
    word-break: break-word;
  }
  .num {
    text-align: center;
  }
  tbody tr:hover {
    background: #fafbfc;
  }

  @media (max-width: 760px) {
    thead {
      display: none;
    }
    tr {
      display: block;
      padding: 10px 0;
      border-bottom: 1px solid #e5e7eb;
    }
    td {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      border: none;
      padding: 6px 4px;
    }
    td::before {
      content: attr(data-label);
      color: #64748b;
      font-size: 12px;
    }
    .num {
      text-align: right;
    }
  }
`;

const FileLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  a,
  button {
    border: none;
    background: none;
    padding: 0;
    font-size: 12.5px;
    color: ${GREEN};
    font-family: ${theme.fonts.primaryFontSemiBold};
    cursor: pointer;
    text-decoration: none;
  }
  a:hover,
  button:hover {
    text-decoration: underline;
  }
`;
